import { Fragment } from 'react';
import { motion } from 'framer-motion';
import { EASE_DRIFT, REVEAL_VIEWPORT } from '../../lib/motion';
import '../../styles/motion/splitHeading.css';

type Tag = 'h1' | 'h2' | 'h3' | 'p';

const TAGS = { h1: motion.h1, h2: motion.h2, h3: motion.h3, p: motion.p } as const;

interface SplitHeadingProps {
    as?: Tag;
    id?: string;
    className?: string;
    /** Wrap a phrase in *asterisks* to set it as a keyword underlined with the brand gradient. */
    text: string;
    /** Small bracketed label that hangs in the first line's indent: "(Over mij)". */
    label?: string;
    /** Drive the animation from outside (e.g. after the loader) instead of on scroll. */
    play?: boolean;
}

interface Char {
    c: string;
    i: number;
}

interface Part {
    kw: boolean;
    lead: boolean;
    trail: boolean;
    words: Char[][];
}

/* Split into parts (plain / keyword), words and characters, numbering characters globally. */
function parse(text: string): { parts: Part[]; total: number; plain: string } {
    let n = 0;
    const parts: Part[] = text
        .split(/(\*[^*]+\*)/)
        .filter(Boolean)
        .map((raw) => {
            const kw = raw.startsWith('*');
            const clean = kw ? raw.slice(1, -1) : raw;
            return {
                kw,
                lead: /^\s/.test(clean),
                trail: /\s$/.test(clean),
                words: clean
                    .split(/\s+/)
                    .filter(Boolean)
                    .map((word) => [...word].map((c) => ({ c, i: n++ }))),
            };
        });
    return { parts, total: n, plain: text.replace(/\*/g, '') };
}

/*
 * Studio X split heading: every letter grows from flat (scaleY 0 to 1) while it
 * fades in, starting at the middle of the text and spreading outward. 1s per
 * letter on the drift curve, 0.5s spread across the whole line. Plays once when
 * the heading reaches 85% of the viewport.
 *
 * Costly (one node per letter): use on one or two statements per page, 8 to 20 words.
 * The real sentence is kept in a visually hidden span for screen readers.
 */
export const SplitHeading = ({ as = 'h2', id, className, text, label, play }: SplitHeadingProps) => {
    const MotionTag = TAGS[as];
    const { parts, total, plain } = parse(text);
    const mid = Math.max((total - 1) / 2, 1);

    const letter = {
        hidden: { opacity: 0, scaleY: 0 },
        show: (i: number) => ({
            opacity: 1,
            scaleY: 1,
            transition: { duration: 1, ease: EASE_DRIFT, delay: (Math.abs(i - mid) / mid) * 0.5 },
        }),
    };

    const trigger =
        play === undefined
            ? { whileInView: 'show', viewport: REVEAL_VIEWPORT }
            : { animate: play ? 'show' : 'hidden' };

    return (
        <MotionTag id={id} className={`split-heading ${className ?? ''}`} initial="hidden" {...trigger}>
            {label && <span className="label split-heading__label">{label}</span>}
            <span className="sr-only">{plain}</span>
            <span aria-hidden="true">
                {parts.map((part, pi) => {
                    const words = part.words.map((word, wi) => (
                        <Fragment key={wi}>
                            {wi > 0 && ' '}
                            <span className="split-heading__word">
                                {word.map(({ c, i }) => (
                                    <motion.span key={i} className="split-heading__char" custom={i} variants={letter}>
                                        {c}
                                    </motion.span>
                                ))}
                            </span>
                        </Fragment>
                    ));
                    return (
                        <Fragment key={pi}>
                            {part.lead && ' '}
                            {part.kw ? <em className="kw">{words}</em> : words}
                            {part.trail && ' '}
                        </Fragment>
                    );
                })}
            </span>
        </MotionTag>
    );
};
